import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import appSelectors from '@/redux/app/appSelectors';

import useMatchMedia from '@/hooks/useMatchMedia';

import leftImg from '@/images/arrow-left-yellow.svg';
import rightImg from '@/images/arrow-right-yellow.svg';

import translate from './translations';
import s from './pagination.module.css';

function Pagination({ list, setPaginatedList, pagination = 6 }) {
  const lang = useSelector(appSelectors.language);

  const { isMobile, isTablet, is1280, is1440 } = useMatchMedia();

  const [total, setTotal] = useState();
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);

  const next = () => {
    if (page < pages) setPage(page + 1);
    else setPage(pages);
  };

  const prev = () => {
    if (page > 1) setPage(page - 1);
    else setPage(1);
  };

  // при получении списка вычисляю параметры пагинации
  useEffect(() => {
    if (list?.length === 0) {
      setTotal(0);
      setPages(1);
      setPage(1);
    } else {
      setTotal(list.length);
      setPages(
        pagination === 1 ? list.length : Math.ceil(list.length / pagination),
      );
      setPage(1);
    }
  }, [list, pagination]);

  // пагинирую массив
  useEffect(() => {
    if (list?.length === 0) {
      setPaginatedList([]);
      return;
    }

    if (list.length + 1 > page * pagination)
      setPaginatedList(list.slice((page - 1) * pagination, page * pagination));
    // проверяю достигнут ли при пагинации конец массива
    else setPaginatedList(list.slice((page - 1) * pagination));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, page]);

  useEffect(() => {
    if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [is1280, is1440, isMobile, isTablet]);

  return (
    <div className={s.pagination}>
      <p className={s.total}>
        {translate['Всего найдено'][lang]}: {total}
      </p>

      {list?.length > 0 && (
        <div className={s.buttons}>
          <button
            type="button"
            onClick={prev}
            className={s.btn_arrow}
            aria-label="prev"
          >
            <img src={leftImg} alt="prev" />
          </button>

          {/* 1 */}
          <button
            type="button"
            onClick={() => setPage(1)}
            className={page === 1 ? s.btn_active : s.btn}
          >
            1
          </button>

          {/* 2 */}
          {((pages > 5 && page <= 3) || (pages <= 5 && pages >= 2)) && (
            <button
              type="button"
              onClick={() => setPage(2)}
              className={page === 2 ? s.btn_active : s.btn}
            >
              2
            </button>
          )}

          {pages > 5 && page > 3 && (
            <button type="button" className={s.btn} onClick={prev}>
              ...
            </button>
          )}

          {/* 3 */}
          {((pages > 5 && page <= 3) || (pages <= 5 && pages >= 3)) && (
            <button
              type="button"
              onClick={() => setPage(3)}
              className={page === 3 ? s.btn_active : s.btn}
            >
              3
            </button>
          )}

          {pages > 5 && page > 3 && page < pages - 2 && (
            <button
              type="button"
              onClick={() => setPage(page)}
              className={s.btn_active}
            >
              {page}
            </button>
          )}

          {pages > 5 && page >= pages - 2 && (
            <button
              type="button"
              onClick={() => setPage(pages - 2)}
              className={page === pages - 2 ? s.btn_active : s.btn}
            >
              {pages - 2}
            </button>
          )}

          {/* 4 */}
          {pages > 5 && page < pages - 2 && (
            <button type="button" className={s.btn} onClick={next}>
              ...
            </button>
          )}

          {pages > 5 && page >= pages - 2 && (
            <button
              type="button"
              onClick={() => setPage(pages - 1)}
              className={page === pages - 1 ? s.btn_active : s.btn}
            >
              {pages - 1}
            </button>
          )}

          {pages >= 4 && pages <= 5 && (
            <button
              type="button"
              onClick={() => setPage(4)}
              className={page === 4 ? s.btn_active : s.btn}
            >
              4
            </button>
          )}

          {/* 5 */}
          {pages >= 5 && (
            <button
              type="button"
              onClick={() => setPage(pages)}
              className={page === pages ? s.btn_active : s.btn}
            >
              {pages}
            </button>
          )}

          <button
            type="button"
            onClick={next}
            className={s.btn_arrow}
            aria-label="next"
          >
            <img src={rightImg} alt="next" />
          </button>
        </div>
      )}
    </div>
  );
}

export default Pagination;

Pagination.propTypes = {
  list: PropTypes.arrayOf(PropTypes.shape).isRequired,
  setPaginatedList: PropTypes.func.isRequired,
  pagination: PropTypes.number,
};
